import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Mail, RefreshCw, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import {
  OfferAcceptedScreen,
  OfferDeclinedScreen,
  OfferLoadingScreen,
  OfferNotFoundScreen,
} from "@/components/offer-status-screens";
import { OfferTemplateIframe } from "@/components/offer-template-iframe";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { renderOfferTemplate } from "@/lib/offer-template-utils";
import { handleOfferMutationError } from "@/lib/offer-verification";
import { orpc, rpcClient } from "@/lib/rpc";

interface OfferDetails {
  id: string;
  jobLevel: string | null;
  salaryBase: string;
  equityShares: number | null;
  signingBonus: string | null;
  performanceBonus: string | null;
  performanceBonusShares: number | null;
  performanceBonusSharesMax: number | null;
  offerLetterTemplate: string | null;
  status: string;
  verificationToken?: string;
  hiringManagerName?: string | null;
  allowPaymixAdjustment?: boolean;
  application?: {
    applicant?: {
      fullName: string;
      email: string;
    };
    jobOpening?: {
      title: string;
      department: string;
      jobLevel: string;
    };
  };
}

export default function OfferView() {
  const [, params] = useRoute("/offer/:token");
  const token = params?.token;
  const [, navigate] = useLocation();
  const [otpCode, setOtpCode] = useState("");
  const [offer, setOffer] = useState<OfferDetails | null>(null);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [step, setStep] = useState<"verify" | "view" | "accepted" | "declined">("verify");
  const [otpSent, setOtpSent] = useState(false);
  const { toast } = useToast();

  // Try to resume session from cookie
  useEffect(() => {
    if (!token) return;

    rpcClient.offers
      .publicOfferSession({ token })
      .then((data) => {
        setOffer(data as unknown as OfferDetails);
        setVerificationToken(data.verificationToken);
        setStep("view");
      })
      .catch(() => {});
  }, [token]);

  const {
    data: offerStatus,
    isLoading,
    error,
  } = useQuery({
    ...orpc.offers.publicOfferStatus.queryOptions({ input: { token: token! } }),
    enabled: !!token,
  });

  const requestOtp = useMutation({
    ...orpc.otp.requestForOffer.mutationOptions(),
    onSuccess: () => {
      setOtpSent(true);
      toast({
        title: "Código enviado",
        description: "Verifique seu email para o código de verificação.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível enviar o código.",
        variant: "destructive",
      });
    },
  });

  const getOfferDetails = useMutation({
    mutationFn: async () => {
      return rpcClient.offers.publicOfferDetails({ token: token!, otpCode });
    },
    onSuccess: (data) => {
      setOffer(data as unknown as OfferDetails);
      setVerificationToken(data.verificationToken);
      setStep("view");
    },
    onError: (error: any) => {
      toast({
        title: "Código inválido",
        description: error.message || "O código está incorreto ou expirou.",
        variant: "destructive",
      });
    },
  });

  const acceptOffer = useMutation({
    mutationFn: async () => {
      return rpcClient.offers.publicAcceptOffer({
        token: token!,
        verificationToken: verificationToken!,
      });
    },
    onSuccess: () => {
      setStep("accepted");
    },
    onError: (error: any) => {
      handleOfferMutationError(error, token, () => {
        toast({
          title: "Erro",
          description: error.message || "Não foi possível aceitar a oferta.",
          variant: "destructive",
        });
      });
    },
  });

  const declineOffer = useMutation({
    mutationFn: async () => {
      return rpcClient.offers.publicDeclineOffer({
        token: token!,
        verificationToken: verificationToken!,
      });
    },
    onSuccess: () => {
      setStep("declined");
    },
    onError: (error: any) => {
      handleOfferMutationError(error, token, () => {
        toast({
          title: "Erro",
          description: error.message || "Não foi possível recusar a oferta.",
          variant: "destructive",
        });
      });
    },
  });

  if (isLoading) {
    return <OfferLoadingScreen />;
  }

  if (error || !offerStatus) {
    return <OfferNotFoundScreen />;
  }

  if (offerStatus.status === "cancelled") {
    return <OfferNotFoundScreen />;
  }

  if (offerStatus.status === "accepted" || step === "accepted") {
    return <OfferAcceptedScreen />;
  }

  if (offerStatus.status === "declined" || step === "declined") {
    return <OfferDeclinedScreen />;
  }

  if (step === "verify" && !offer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-lg bg-primary mx-auto">
              <Mail className="h-7 w-7 text-primary-foreground" />
            </div>
            <CardTitle>Verificação de Identidade</CardTitle>
            <CardDescription>
              Para visualizar sua oferta de emprego, precisamos verificar seu email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {offerStatus.applicantName && (
              <div className="text-center text-sm text-muted-foreground">
                <p>
                  Olá, <strong>{offerStatus.applicantName}</strong>
                </p>
                {offerStatus.email && (
                  <p>
                    Email: <strong>{offerStatus.email}</strong>
                  </p>
                )}
              </div>
            )}

            {!otpSent && (
              <Button
                onClick={() => token && requestOtp.mutate({ token })}
                className="w-full"
                disabled={requestOtp.isPending}
                data-testid="button-send-otp"
              >
                {requestOtp.isPending ? "Enviando..." : "Enviar código de verificação"}
              </Button>
            )}

            {otpSent && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">Código de verificação</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength={6}
                    className="text-center text-2xl tracking-widest"
                    data-testid="input-otp"
                  />
                </div>
                <Button
                  onClick={() => getOfferDetails.mutate()}
                  className="w-full"
                  disabled={otpCode.length !== 6 || getOfferDetails.isPending}
                  data-testid="button-verify-otp"
                >
                  {getOfferDetails.isPending ? "Verificando..." : "Verificar"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => token && requestOtp.mutate({ token })}
                  className="w-full"
                  disabled={requestOtp.isPending}
                  data-testid="button-resend-otp"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reenviar código
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!offer) {
    return null;
  }

  const applicant = offer.application?.applicant;
  const jobOpening = offer.application?.jobOpening;

  const renderedTemplate = renderOfferTemplate(offer, {
    candidateName: applicant?.fullName || "Candidato",
    jobTitle: jobOpening?.title || "N/A",
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="sticky top-0 z-10 bg-background border-b py-4">
        <div className="flex gap-4 max-w-md mx-auto px-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => declineOffer.mutate()}
            disabled={declineOffer.isPending || !verificationToken}
            data-testid="button-decline-offer"
          >
            <XCircle className="w-4 h-4 mr-2" />
            {declineOffer.isPending ? "Processando..." : "Recusar"}
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              if (offer.allowPaymixAdjustment && (offer.equityShares ?? 0) > 0) {
                navigate(`/accept-offer/${token}/paymix`);
              } else {
                acceptOffer.mutate();
              }
            }}
            disabled={acceptOffer.isPending || !verificationToken}
            data-testid="button-accept-offer"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {acceptOffer.isPending ? "Processando..." : "Aceitar Oferta"}
          </Button>
        </div>
      </div>

      <OfferTemplateIframe html={renderedTemplate} />
    </div>
  );
}
